import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, tokens } from '../api/client';
import { useAuth } from './AuthProvider';

type McpTokenState = {
  /** `loading` until the first check settles, then `ready`. */
  status: 'loading' | 'ready';
  /**
   * True when at least one token can still be used. A revoked or expired token
   * does not count: the client would be refused, which is the situation the
   * redirect is meant to catch.
   */
  hasActiveToken: boolean;
  /** Re-reads the token list, e.g. right after one is created or revoked. */
  refresh(): Promise<void>;
};

const McpTokenContext = createContext<McpTokenState | null>(null);

/**
 * Whether this account has a usable MCP token.
 *
 * The result is cached here rather than refetched per navigation, so the guard
 * that redirects to the token page does not re-query the API on every route
 * change. Pages that mint or revoke a token call `refresh()` to keep it
 * accurate.
 */
export function McpTokenProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<McpTokenState['status']>('loading');
  const [hasActiveToken, setHasActiveToken] = useState(false);

  const refresh = useCallback(async () => {
    if (!tokens.access()) {
      setHasActiveToken(false);
      setStatus('ready');
      return;
    }
    try {
      const list = await api.mcp.tokens();
      setHasActiveToken(list.some((t) => !t.revoked && !t.expired));
    } catch {
      // A failed check must not lock the user out of the app. Assume a token
      // exists and let the page they land on report the real error.
      setHasActiveToken(true);
    } finally {
      setStatus('ready');
    }
  }, []);

  // Re-check whenever the signed-in account changes, so signing in as someone
  // else does not inherit the previous account's answer.
  useEffect(() => {
    setStatus('loading');
    void refresh();
  }, [user?.id, refresh]);

  const value = useMemo<McpTokenState>(
    () => ({ status, hasActiveToken, refresh }),
    [status, hasActiveToken, refresh],
  );

  return <McpTokenContext.Provider value={value}>{children}</McpTokenContext.Provider>;
}

export function useMcpTokens(): McpTokenState {
  const context = useContext(McpTokenContext);
  if (!context) throw new Error('useMcpTokens must be used inside an McpTokenProvider');
  return context;
}
